import React from 'react';
import { connect } from 'react-redux';
import { noop } from 'lodash';
import { bindActionCreators } from 'redux';

import addStylist from 'actions/stylist/addStylist.action';
import editStylist from 'actions/stylist/editStylist.action';
import validateName from 'bl/validation/validateName.bl';
import validateNameLength from 'bl/validation/validateNameLength.bl';
import LINK from 'constants/link.const';
import PRICING_TYPE from 'constants/pricingType.const';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToSpacelessString from 'utils/converters/fromAnyToSpacelessString.util';
import fromObjectToQueryString from 'utils/converters/object/fromObjectToQueryString.util';
import { fromPropsToQuery, fromPropsToQueryString } from 'utils/converters/props/path.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import { isNotValidId } from 'utils/predicates/fields.util';

import PopupModalContainer from 'common/PopupModal';
import { validateEmailWithDomain } from 'lib/validators';
import HeaderContainer from 'onboarding/common/Header';
import OnboardingFooter from 'onboarding/common/OnboardingFooter';
import { clearStylistState } from 'onboarding/stylists/actions';
import AddStylistOnboarding from 'onboarding/stylists/AddStylistOnboarding';

const L = logger('AddStylistWrap');

class AddStylistWrap extends React.Component {
  constructor(props) {
    super(props);
    const { roles, location, categories, errorMessage } = props;

    const stylist = asObject(location.state).stylist || {
      user_id: '',
      user_name: '',
      email: '',
      phone: '',
      status: 'active',
      gender: 'female',
      role: roles[0],
      serves_as_waitlist: false,
      is_self_employed: false,
      permission_level: '',
    };

    const isFooterButtonActive = fromAnyToBool(location.state);

    const categoryExecutions = asArray(stylist.categories || categories)
      .filter(({ id }) => id)
      .map(({ id }) => ({ category: id }));

    this.state = { isFooterButtonActive, stylist, nameHasError: false, categoryExecutions, errorMessage };
  }

  UNSAFE_componentWillMount() {
    const { currentSalon } = this.props;
    const salonId = asObject(currentSalon).id || fromPropsToQuery(this.props).salonId;

    // used to be  !currentSalon
    if (isNotValidId(salonId)) {
      navFor('AddStylistWrap.componentWillMount()', this.props).push({
        pathname: LINK.onboardingType,
        search: fromPropsToQueryString(this.props),
      });
    }
  }

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { clearStylistState } = this.props;
    const { isFetching, isSaved, errorMessage, isPosting } = nextProps;
    const shouldAddStylist = !isFetching && !isPosting && isSaved;

    if (shouldAddStylist) {
      clearStylistState();
      navFor('AddStylistWrap.componentWillReceiveProps()', this.props).push({
        pathname: LINK.onboardingStylists,
        search: fromPropsToQueryString(this.props),
      });
    }

    if (errorMessage) {
      this.setState({ errorMessage });
    }
  }

  onFooterButtonClick = () => {
    const { currentSalon, location, editStylist, addStylist } = this.props;
    const { stylist, isFooterButtonActive } = this.state;

    const salonId = asObject(currentSalon).id || fromPropsToQuery(this.props).salonId;
    const locationState = location.state;

    L.debug('onFooterButtonClick()', { isFooterButtonActive, salonId, locationState });

    if (!isFooterButtonActive) {
      return;
    }

    if (locationState) {
      L.unignore('onFooterButtonClick()', 'editStylist:', editStylist({ stylist, salonId })).then((result) => {
        L.debug('onFooterButtonClick()', 'editStylist result:', result);
        navFor('AddStylistWrap.onFooterButtonClick()', this.props).push({
          search: fromObjectToQueryString({
            ...fromPropsToQuery(this.props),
            stylistId: getAnyOrVoid('response.id', result),
          }),
        });
      });
    } else {
      L.unignore('onFooterButtonClick()', 'addStylist:', addStylist({ stylist, salonId })).then((result) => {
        L.debug('onFooterButtonClick()', 'addStylist result:', result);
        navFor('AddStylistWrap.onFooterButtonClick()', this.props).push({
          search: fromObjectToQueryString({
            ...fromPropsToQuery(this.props),
            stylistId: getAnyOrVoid('response.id', result),
          }),
        });
      });
    }
  };

  onRoleChange = (roleId) => {
    const { roles } = this.props;
    const stylist = { ...this.state.stylist };

    for (let i = 0; i < roles.length; i += 1) {
      if (roleId === roles[i].id) {
        stylist.role = roles[i];
        break;
      }
    }

    this.setState({ stylist }, () => this.checkButton());
  };

  onNameLostFocus = (userName) =>
    void this.setState({ stylist: { ...this.state.stylist, user_name: userName } }, () => this.checkButton());

  onPhoneLostFocus = (phone) =>
    void this.setState({ stylist: { ...this.state.stylist, phone } }, () => this.checkButton());

  onEmailLostFocus = (email) =>
    void this.setState({ stylist: { ...this.state.stylist, email } }, () => this.checkButton());

  onCategoryCheckboxChange = (categoryId) => {
    const categoryExecutions = this.state.categoryExecutions.slice();
    for (let i = 0; i < categoryExecutions.length; i += 1) {
      if (categoryExecutions[i].category === categoryId) {
        categoryExecutions.splice(i, 1);
        this.setState({ categoryExecutions }, () => this.checkButton());
        return;
      }
    }
    categoryExecutions.push({
      category: categoryId,
    });
    this.setState({ categoryExecutions }, () => this.checkButton());
  };

  onSelfEmployedChange = (selfEmployed) => {
    const editedStylist = Object.assign({}, this.state.stylist);
    editedStylist.is_self_employed = selfEmployed;
    this.setState({ stylist: editedStylist });
  };

  onWaitlistChange = (isWaitlist) => {
    const editedStylist = Object.assign({}, this.state.stylist);
    editedStylist.serves_as_waitlist = isWaitlist;
    this.setState({ stylist: editedStylist });
  };

  validateName = (value) => {
    if (validateName({ value })) {
      this.setState({ nameHasError: false });
      return '';
    }
    this.setState({ nameHasError: false });
    return 'Please enter a name';
  };

  validateNameLength = (value) => validateNameLength({ value });

  validateEmail = (value) => {
    if (value === '') {
      return '';
    }
    if (validateEmailWithDomain(value)) {
      // this.setState({ emailHasError: false });
      return '';
    }
    // this.setState({ emailHasError: true });
    return 'Please enter a valid email';
  };

  checkButton = () => {
    const { stylist, nameHasError } = this.state;
    this.setState({ isFooterButtonActive: !nameHasError && fromAnyToSpacelessString(stylist.user_name).length !== 0 });
  };

  hideError = () => void this.setState({ errorMessage: '' });

  render() {
    const { stylist, categoryExecutions, errorMessage, isFooterButtonActive } = this.state;
    const { isPosting, currentSalon, isFetching, stylists, categories, roles: rolesFromProps } = this.props;

    const pricing = L.debug('render()', 'pricing:', asObject(currentSalon).pricing_type || PRICING_TYPE.unknown);
    const roles = L.debug('render()', 'roles:', pricing === PRICING_TYPE.role ? rolesFromProps : undefined);

    const description = isFullArray(stylists)
      ? 'Add another staff member.'
      : 'Let’s add your first staff member and select the services they offer.';

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        data-bem="AddStylistWrap"
        className="content-wrap"
        onKeyPress={(event) => {
          if (event.key === 'Enter') this.onFooterButtonClick();
        }}
      >
        {errorMessage && (
          <PopupModalContainer
            popupTitle="Error"
            popupMessage={errorMessage}
            buttonText="Okay"
            buttonCallback={this.hideError}
          />
        )}

        <HeaderContainer titleFirstPart="Add Your Team" description={description} />

        <AddStylistOnboarding
          stylist={stylist}
          roles={roles}
          categories={categories}
          onNameChange={this.onNameLostFocus}
          validateName={this.validateName}
          validateNameLength={this.validateNameLength}
          onPhoneChange={this.onPhoneLostFocus}
          onEmailChange={this.onEmailLostFocus}
          validateEmail={this.validateEmail}
          onRoleChange={this.onRoleChange}
          onCategoryCheckboxChange={this.onCategoryCheckboxChange}
          onServiceCheckboxChange={noop}
          categoryExecutions={categoryExecutions}
          pricingType={pricing}
          onWaitlistChange={(value) => void this.onWaitlistChange(value)}
          onSelfEmployedChange={(value) => void this.onSelfEmployedChange(value)}
        />

        <OnboardingFooter
          isFooterButtonActive={isFooterButtonActive}
          onButtonClick={this.onFooterButtonClick}
          isLoading={isFetching || isPosting}
        />

        {(isPosting || isFetching) && <div data-bem="AddStylistWrap__load-blocker" className="load-blocker" />}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  ...state.onboarding.roles,
  ...state.onboarding.stylists,
  currentSalon: state.currentSalon,
  categories: state.onboarding.services.categories,
  isFetching: state.onboarding.stylists.isFetching,
  isPosting: state.onboarding.stylists.isPosting,
  isSaved: state.onboarding.stylists.isSaved,
  errorMessage: state.onboarding.stylists.errorMessage,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ addStylist, editStylist, clearStylistState }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AddStylistWrap);
